import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Edit2, Image, Plus, Power, Save, Search, Trash2, Upload, X } from 'lucide-react';
import { API_BASE, getImageUrl } from '../utils/helpers';
import { useNotify } from '../components/NotificationProvider';
import {
  actualizarImagenCarrusel,
  cambiarEstadoImagenCarrusel,
  crearImagenCarrusel,
  eliminarImagenCarrusel,
  obtenerCarruselAdmin
} from '../api/carrusel';

const API_UPLOAD_URL = `${API_BASE}/api/upload`;
const MAX_IMAGES = 6;

const emptyForm = {
  Titulo: '',
  UrlImagen: '',
  Orden: '',
  Activa: true
};

const normalize = (value) => String(value || '').trim();
const isActive = (value) => value === true || value === 1 || value === '1' || String(value).toLowerCase() === 'true';

const AdminCarousel = ({ user }) => {
  const { success: notifySuccess, error: notifyError, info: notifyInfo } = useNotify();
  const [imagenes, setImagenes] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');

  const cargarImagenes = async () => {
    try {
      setLoading(true);
      const data = await obtenerCarruselAdmin();
      setImagenes(Array.isArray(data) ? data : []);
    } catch (err) {
      notifyError(err.message || 'No se pudo cargar el carrusel', { duration: 4500 });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    cargarImagenes();
  }, []);

  useEffect(() => {
    return () => {
      if (imagePreview && imagePreview.startsWith('blob:')) {
        URL.revokeObjectURL(imagePreview);
      }
    };
  }, [imagePreview]);

  const filtradas = imagenes.filter((item) => {
    const q = normalize(searchTerm).toLowerCase();
    if (!q) return true;
    return [item.Titulo, item.UrlImagen]
      .some((value) => normalize(value).toLowerCase().includes(q));
  });

  const resetPreview = () => {
    if (imagePreview && imagePreview.startsWith('blob:')) {
      URL.revokeObjectURL(imagePreview);
    }
    setImagePreview('');
    setImageFile(null);
  };

  const openCreate = () => {
    if (imagenes.length >= MAX_IMAGES) {
      notifyInfo(`El carrusel permite maximo ${MAX_IMAGES} imagenes`, { duration: 4000 });
      return;
    }
    setEditing(null);
    setForm({
      ...emptyForm,
      Orden: String(imagenes.length + 1)
    });
    resetPreview();
    setShowForm(true);
  };

  const openEdit = (imagen) => {
    setEditing(imagen);
    setForm({
      Titulo: imagen.Titulo || '',
      UrlImagen: imagen.UrlImagen || '',
      Orden: String(imagen.Orden ?? ''),
      Activa: isActive(imagen.Activa)
    });
    resetPreview();
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditing(null);
    setForm(emptyForm);
    resetPreview();
  };

  const uploadImageIfNeeded = async () => {
    if (!imageFile) return form.UrlImagen;

    const fd = new FormData();
    fd.append('image', imageFile);
    const res = await fetch(API_UPLOAD_URL, {
      method: 'POST',
      body: fd
    });
    if (!res.ok) {
      const text = await res.text().catch(() => '');
      throw new Error(text || `HTTP ${res.status}`);
    }
    const data = await res.json();
    return data.relativeUrl || data.imageUrl || '';
  };

  const handleFileChange = (file) => {
    resetPreview();
    if (!file) return;
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (saving) return;

    try {
      setSaving(true);
      const imageUrl = await uploadImageIfNeeded();
      if (!normalize(imageUrl)) {
        notifyError('Selecciona una imagen', { duration: 4000 });
        return;
      }

      const payload = {
        Titulo: normalize(form.Titulo),
        UrlImagen: imageUrl,
        Orden: Number(form.Orden) || 0,
        Activa: !!form.Activa
      };

      if (editing?.Id) {
        await actualizarImagenCarrusel(editing.Id, payload, user);
        notifySuccess('Imagen actualizada', { duration: 3000 });
      } else {
        await crearImagenCarrusel(payload, user);
        notifySuccess('Imagen agregada', { duration: 3000 });
      }

      closeForm();
      await cargarImagenes();
    } catch (err) {
      notifyError(err.message || 'No se pudo guardar la imagen', { duration: 4500 });
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (imagen) => {
    try {
      setSaving(true);
      await cambiarEstadoImagenCarrusel(imagen.Id, !isActive(imagen.Activa), user);
      await cargarImagenes();
      notifySuccess(isActive(imagen.Activa) ? 'Imagen desactivada' : 'Imagen activada', { duration: 3000 });
    } catch (err) {
      notifyError(err.message || 'No se pudo cambiar el estado', { duration: 4500 });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (imagen) => {
    try {
      setSaving(true);
      await eliminarImagenCarrusel(imagen.Id, user);
      await cargarImagenes();
      notifySuccess('Imagen eliminada', { duration: 3000 });
    } catch (err) {
      notifyError(err.message || 'No se pudo eliminar la imagen', { duration: 4500 });
    } finally {
      setSaving(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.45 }}
    >
      <div className="mb-8 flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="mb-2 text-3xl font-bold text-gray-900">Carrusel de servicios</h1>
          <p className="text-gray-600">Administra hasta {MAX_IMAGES} imagenes para la pagina principal</p>
        </div>

        <button
          type="button"
          onClick={openCreate}
          disabled={imagenes.length >= MAX_IMAGES}
          className="flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-pink-500 to-purple-600 px-5 py-3 font-bold text-white shadow-lg disabled:opacity-50"
        >
          <Plus className="h-5 w-5" />
          Agregar imagen
        </button>
      </div>

      <div className="rounded-lg border border-pink-100 bg-white/90 p-5 shadow-lg">
        <div className="mb-5 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Buscar imagen..."
              className="w-full rounded-xl border-2 border-gray-200 py-3 pl-12 pr-4 focus:border-pink-500 focus:outline-none"
            />
          </div>
          <span className="rounded-lg bg-pink-50 px-4 py-2 text-sm font-semibold text-purple-700">
            {imagenes.length}/{MAX_IMAGES} imagenes
          </span>
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          {loading ? (
            <div className="col-span-full py-10 text-center text-gray-500">Cargando...</div>
          ) : filtradas.length === 0 ? (
            <div className="col-span-full py-10 text-center text-gray-500">Sin imagenes</div>
          ) : (
            filtradas.map((imagen) => {
              const activa = isActive(imagen.Activa);
              return (
                <div key={imagen.Id} className="overflow-hidden rounded-lg border border-gray-100 bg-white shadow-sm">
                  <img
                    src={getImageUrl(imagen.UrlImagen)}
                    alt={imagen.Titulo || 'Imagen carrusel'}
                    className="h-44 w-full object-cover"
                    onError={(event) => {
                      event.currentTarget.onerror = null;
                      event.currentTarget.src = getImageUrl('');
                    }}
                  />
                  <div className="space-y-3 p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <h3 className="truncate font-semibold text-gray-900">{imagen.Titulo || 'Sin titulo'}</h3>
                        <p className="text-sm text-gray-500">Orden {imagen.Orden ?? 0}</p>
                      </div>
                      <span className={`rounded-lg px-2 py-1 text-xs font-bold ${activa ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                        {activa ? 'Activa' : 'Inactiva'}
                      </span>
                    </div>

                    <div className="flex justify-end gap-2">
                      <button
                        type="button"
                        onClick={() => openEdit(imagen)}
                        className="rounded-lg bg-blue-100 p-2 text-blue-700 hover:bg-blue-200"
                        title="Editar"
                      >
                        <Edit2 className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleToggle(imagen)}
                        disabled={saving}
                        className={`rounded-lg p-2 disabled:opacity-50 ${activa ? 'bg-gray-100 text-gray-700 hover:bg-gray-200' : 'bg-green-100 text-green-700 hover:bg-green-200'}`}
                        title={activa ? 'Desactivar' : 'Activar'}
                      >
                        <Power className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDelete(imagen)}
                        disabled={saving}
                        className="rounded-lg bg-red-100 p-2 text-red-700 hover:bg-red-200 disabled:opacity-50"
                        title="Eliminar"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <form
            onSubmit={handleSubmit}
            noValidate
            className="w-full max-w-xl rounded-lg border border-purple-200 bg-white p-6 shadow-xl"
          >
            <div className="mb-5 flex items-center justify-between">
              <h2 className="text-xl font-bold text-purple-700">
                {editing ? 'Editar imagen' : 'Agregar imagen'}
              </h2>
              <button
                type="button"
                onClick={closeForm}
                className="rounded-lg bg-gray-100 p-2 text-gray-700 hover:bg-gray-200"
                title="Cerrar"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="mb-2 block text-sm font-semibold text-gray-700">Titulo</label>
                <input
                  type="text"
                  value={form.Titulo}
                  onChange={(e) => setForm((prev) => ({ ...prev, Titulo: e.target.value }))}
                  className="w-full rounded-xl border-2 border-gray-200 px-4 py-3 focus:border-pink-500 focus:outline-none"
                  maxLength={100}
                  placeholder="Opcional"
                />
              </div>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <label className="mb-2 block text-sm font-semibold text-gray-700">Orden</label>
                  <input
                    type="number"
                    min="0"
                    value={form.Orden}
                    onChange={(e) => setForm((prev) => ({ ...prev, Orden: e.target.value }))}
                    className="w-full rounded-xl border-2 border-gray-200 px-4 py-3 focus:border-pink-500 focus:outline-none"
                  />
                </div>
                <label className="flex items-end gap-2 pb-3 text-sm font-semibold text-gray-700">
                  <input
                    type="checkbox"
                    checked={!!form.Activa}
                    onChange={(e) => setForm((prev) => ({ ...prev, Activa: e.target.checked }))}
                    className="h-4 w-4 accent-pink-600"
                  />
                  Activa
                </label>
              </div>

              <div>
                <label className="mb-2 block text-sm font-semibold text-gray-700">Imagen</label>
                <label className="flex cursor-pointer items-center justify-center gap-2 rounded-xl border-2 border-dashed border-pink-200 px-4 py-5 text-purple-700 hover:border-pink-400 hover:bg-pink-50">
                  <Upload className="h-5 w-5" />
                  <span>{imageFile ? imageFile.name : 'Seleccionar imagen'}</span>
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => handleFileChange(e.target.files?.[0])}
                  />
                </label>
                {(imagePreview || form.UrlImagen) && (
                  <img
                    src={imagePreview || getImageUrl(form.UrlImagen)}
                    alt="Vista previa"
                    className="mt-3 h-40 w-full rounded-lg border object-cover"
                    onError={(event) => {
                      event.currentTarget.onerror = null;
                      event.currentTarget.src = getImageUrl('');
                    }}
                  />
                )}
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={closeForm}
                className="rounded-xl bg-gray-200 px-4 py-2 text-gray-700 hover:bg-gray-300"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={saving}
                className="flex items-center gap-2 rounded-xl bg-purple-600 px-4 py-2 text-white hover:bg-purple-700 disabled:opacity-50"
              >
                {editing ? <Save className="h-4 w-4" /> : <Image className="h-4 w-4" />}
                Guardar
              </button>
            </div>
          </form>
        </div>
      )}
    </motion.div>
  );
};

export default AdminCarousel;
